/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import * as logger from './SocketLogger';
import {
  ConnectionStatus,
  EntitySubscription,
  ObservableEntityId,
  SocketConfiguration,
  SocketConnection,
  SubscriptionAction
} from './types';

/**
 * The Socket API provides underlying connection to a single WebSocket within an
 * application. Socket instances are not to be used directly, rather, clients are
 * to subscribe to the higher-level EntityObserver API which manages subscriptions
 * while ensuring only a single connection is established.
 */
export default class Socket implements SocketConnection {
  /**
   * Provides a reference to the underlying native WebSocket instance through
   * which all messages and sent and retrieved.
   */
  private socket: WebSocket;

  /**
   * Provides a reference to the configuration options provided during instantiation.
   */
  private config: SocketConfiguration;

  /**
   * Provides a reference to the constructed url during instantiation of
   * the Socket instance. The endpoint is based on the WebSocket service
   * url, authentication token, and appKey provided within the initialization
   * configuration.
   */
  private endpoint: string;

  /**
   * Provides a mapping of WebSocket events and their corresponding event
   * listeners on the Socket instance.
   */
  private listeners: [string, any][];

  /**
   * Provides a reference to the reconnection identifier which is assigned
   * by the interval instance when a reconnection attempt is in progress,
   * and subsequently used to clear the interval once the reconnection has
   * been successfully established.
   */
  private reconnectId: any;

  /**
   * Defines the current (if any) reconnection attempt increment. This value
   * is used to specify the number of attempts required until the connection
   * has successfully reconnected.
   */
  private reconnectionAttempt = 0;

  /**
   * Provides a reference to the reconnection identifier which is assigned
   * by the keep alive timeout instance.
   */
  private keepAliveId: any;

  /**
   * Provides the initialization entry point onto which the underlying Web Socket
   * connection is established based on the provided `SocketConfiguration`.
   */
  initialize(configuration: SocketConfiguration, force = false): boolean {
    if (!this.config || (this.config && configuration && force)) {
      try {
        this.endpoint = Socket.constructURL(configuration);
        this.config   = configuration;
        this.socket   = new WebSocket(this.endpoint);
        this._initializeListeners();
      } catch (err) {
        logger.error(err, this);
      }
      return true;
    }
    return false;
  };

  /**
   * Provides a convenience which determines if the Socket is currently connected.
   *
   * ```typescript
   *   import EntityObserver from '@ion/services/src/websocket';
   *
   *   if (EntityObserver.isConnected) {
   *     ...
   *   }
   * ```
   *
   * @returns true if the connection is currently open, otherwise, false.
   */
  get isConnected(): boolean {
    return this.socket?.readyState === ConnectionStatus.OPEN;
  };

  /**
   * Allows for adding a new change observer via the underlying WebSocket's
   * subscribe  message.
   *
   * @param   entity The name of the entity for which the observer is to be
   *          made.
   * @param   ids An array of entity ids for which to be notified for changes.
   * @returns true if the subscribe message has been sent to the underlying
   *          WebSocket instance, otherwise, false. The only case where the
   *          observer message would not be sent is if the connection is
   *          not open.
   */
  subscribe(entity: EntitySubscription, ids: ObservableEntityId[]): boolean {
    return this.subscription(SubscriptionAction.subscribe, entity, ids);
  };

  /**
   * Allows for removing a previously added observer from the underlying
   * WebSocket's subscribe message.
   *
   * @param   entity The name of the entity for which to add the observer.
   * @param   ids An array of entity ids for which to no longer be notified for
   *          changes.
   * @returns true if the unsubscribe message has been sent to the underlying
   *          WebSocket instance, otherwise, false. The only case where the
   *          observer message would not be sent is if the connection is
   *          not open.
   */
  unsubscribe(entity: EntitySubscription, ids: ObservableEntityId[]): boolean {
    return this.subscription(SubscriptionAction.unsubscribe, entity, ids);
  };

  /**
   * @private
   *
   * Provides an abstraction which is used for sending a new subscription message
   * to the underlying WebSocket Connection.
   */
  private subscription(action: SubscriptionAction, entity: EntitySubscription, ids: ObservableEntityId[]): boolean {
    if (this.isConnected) {
      this.socket.send(JSON.stringify({
        authToken : this.config.authToken,
        data      : {
          entities : [{name: entity, ids: ids.map(id => String(id))}]
        },
        action
      }));
      return true;
    }
    return false;
  }

  /**
   * @private
   *
   * Invoked during initialization at which point listeners are added to the
   * underlying WebSocket for all states. If the destruct argument is set to
   * true, the listeners will be removed; effectively the inverse operation.
   */
  private _initializeListeners(destruct = false): void {
    if (!this.listeners) {
      this.listeners = Object.entries({
        open    : this.onConnectionEstablished,
        close   : this.onConnectionClosed,
        error   : this.onConnectionError,
        message : this.onDataReceived
      });
    }
    this.listeners.forEach(([event, listener]) => {
      this.socket.removeEventListener(event, listener);
      if (!destruct) {
        this.socket.addEventListener(event, listener);
      }
    });
    if (destruct) {
      this.listeners = null;
    }
  };

  /**
   * @private
   *
   * Invoked when the underlying WebSocket Connection has been established,
   * invoking the `onConnect` configuration option (if provided).
   */
  private onConnectionEstablished = (event: Event): void => {
    logger.connected(event, this);
    this.config.onConnect?.(event);
    this.initializeKeepAlive();
  };

  /**
   * @private
   *
   * Invoked when the underlying WebSocket Connection has been closed, invoking
   * the `onDisconnect` configuration option (if provided). In addition, if the
   * `disableAutoReconnect` is not explicitly set to false (defaults to true),
   * the API will attempt to re-establish the connection.
   */
  private onConnectionClosed = (event: CloseEvent): void => {
    logger.closed(event, this);
    this.config.onDisconnect?.(event);
    if (!this.config.disableAutoReconnect) {
      this.reconnect();
    }
  };

  /**
   * @private
   *
   * Invoked when an error occurs within the underlying WebSocket connection,
   * invoking the `onConnectionError` configuration option (if provided).
   */
  private onConnectionError = (event: ErrorEvent): void => {
    logger.error(event, this);
    this.config.onConnectionError?.(event);
  };

  /**
   * @private
   *
   * Invoked when a message is received from the underlying WebSocket connection,
   * at which point the message data is parsed to a JSON object and the `onData`
   * configuration option is invoked with the data message.
   */
  private onDataReceived = (event: MessageEvent): void => {
    logger.data(event, this);
    this.config.onData?.(safeJSONParse(event.data));
  };

  /**
   * @private
   *
   * Attempts to reconnect a previously established connection which has been
   * closed by the server. A reconnection attempt is made based on the value
   * of the RECONNECTION_INTERVAL (every second). To disable auto-reconnection
   * attempts, the `disableAutoReconnect` option can be set to false during
   * initialization.
   */
  private reconnect(): void {
    const {disableAutoReconnect, reconnectionInterval} = this.config;
    if (!disableAutoReconnect && !this.isConnected) {
      this.reconnectId = setInterval(() => {
        if (!this.isConnected) {
          logger.reconnect(++this.reconnectionAttempt, this);
          this._initializeListeners(true);
          this.socket = new WebSocket(this.endpoint);
        } else {
          logger.reconnected(this.reconnectionAttempt, this);
          this.config.onReconnection?.(this.reconnectionAttempt);
          clearInterval(this.reconnectId);
          this.reconnectionAttempt = 0;
          this.reconnectId         = null;
          this._initializeListeners();
        }
      }, reconnectionInterval || Socket.RECONNECTION_INTERVAL);
    }
  };

  /**
   * @private
   *
   * Invoked when the connection has been established, at which point the keep alive
   * interval is invoked to ensure the connection remains open until explicitly closed.
   */
  private initializeKeepAlive(): void {
    if (!this.config.disableKeepAlive) {
      clearInterval(this.keepAliveId);
      this.keepAliveId = setInterval(() => {
        this.socket.send(JSON.stringify({
          authToken : this.config.authToken
        }));
      }, Socket.KEEP_ALIVE_INTERVAL);
    }
  }

  /**
   * Allows for closing the underlying WebSocket Connection while also effectively
   * nullifying this instance, including all listeners, etc. Generally, this method
   * should not be needed; however, it exists for cases where an application must
   * ensure that the connection is closed and all listeners are removed.
   */
  _destroy(): void {
    clearTimeout(this.reconnectId);
    this.config.disableAutoReconnect = true;
    this._initializeListeners(true);
    this.socket.close();
    this.socket = null;
  };

  /**
   * Defines the default reconnection attempt interval, which, by default, is set
   * once every second.
   */
  private static RECONNECTION_INTERVAL = 1000;

  /**
   * Defines the default keep alive interval, which is used to ensure the connection
   * remains open until explicitly closed. By default, this is set to once every nine
   * (9) minutes as connections are closed after 10 minutes of inactivity.
   */
  private static KEEP_ALIVE_INTERVAL = 540000;

  /**
   * Constructs the WebSocket Endpoint based on the provide configuration options.
   *
   * @param   The SocketConfiguration options which provide the parameters for
   *          which the URL is to be constructed.
   * @returns The Endpoint URL based on the provided options parameters.
   */
  private static constructURL({url, preauthToken, appKey}: SocketConfiguration): string {
    return encodeURI(`${url}?Authorization=${preauthToken}&x-app-key=${appKey}`);
  }
};
